import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useApi } from "@platform/api";
import { type SignedInUser } from "@platform/user";
import { useSignedInUser } from "@platform/user/client";

import { RFPDetailsState } from "../pages/RFPDetails/types";
import envVariables from "../projectEnvVariables";

// Timings come from ADR-039 (10.39), "Constants and where they come from".
const HEARTBEAT_INTERVAL_MS = 15_000;
const IDLE_TIMEOUT_MS = 60_000;
const REQUEST_TIMEOUT_MS = 5_000;
const FIRST_BACKOFF_MS = 30_000;
const MAX_BACKOFF_MS = 5 * 60_000;

const HEARTBEATS_PATH = "/time-tracker/v1/heartbeats";

const INPUT_EVENTS = [
  "mousemove",
  "pointerdown",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
] as const;
// capture: scroll does not bubble, and grids scroll inside their own containers.
const LISTENER_OPTIONS = { passive: true, capture: true } as const;

type RfpDetailsData = RFPDetailsState["data"];

interface RfpActivity {
  readonly rfpId: number;
  readonly rfpStatus: string;
  readonly isAssigned: boolean;
}

// Deployed environments serve production builds and always track. The local
// dev server tracks only when Tilt runs time-tracker and sets the variable.
const isTimeTrackingEnabled = (): boolean =>
  !import.meta.env.DEV || envVariables.VITE_TIME_TRACKING_ENABLED === "true";

// Centro users are assigned through the Centro analyst slot, broker_analysts
// through assignedBrokerAnalyst. See ADR 10.19.
const isAssignedToUser = (
  data: NonNullable<RfpDetailsData>,
  user: SignedInUser,
): boolean => {
  const userEmail = user.email?.toLowerCase();
  const assigneeEmail = user.isBrokerAnalyst
    ? data.rfp?.details.assignedBrokerAnalyst
    : data.rfpAnalyst?.email;
  return !!userEmail && assigneeEmail?.toLowerCase() === userEmail;
};

const toRfpActivity = (
  data: RfpDetailsData,
  user: SignedInUser,
): RfpActivity | undefined =>
  data?.rfp
    ? {
        rfpId: Number(data.rfp.id),
        rfpStatus: data.rfp.details.status,
        isAssigned: isAssignedToUser(data, user),
      }
    : undefined;

/**
 * Sends a heartbeat to time-tracker every 15 seconds while the user is active
 * on an RFP page, so dbt can total active time per user and RFP (ADR-039).
 * Tracking never re-renders the page, never retries, and never surfaces an error.
 */
export const useRfpActivityHeartbeat = (data: RfpDetailsData): void => {
  const axiosInstance = useApi();
  const user = useSignedInUser();
  const { pathname } = useLocation();

  const activityRef = useRef(toRfpActivity(data, user));
  activityRef.current = toRfpActivity(data, user);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    if (!isTimeTrackingEnabled()) {
      return undefined;
    }

    let lastInputAt = Date.now();
    // Stays set until a resume heartbeat succeeds, so a retry after a failed
    // resume can't land within dbt's credit gap and credit the break.
    let isResumePending = false;
    let isInFlight = false;
    let backoffMs = 0;
    let backoffUntil = 0;

    const isIdle = (now: number): boolean =>
      now - lastInputAt >= IDLE_TIMEOUT_MS;
    const isHidden = (): boolean => document.visibilityState === "hidden";

    const sendHeartbeat = async (): Promise<void> => {
      const activity = activityRef.current;
      if (!activity || isHidden() || isInFlight || Date.now() < backoffUntil) {
        return;
      }

      const isResume = isResumePending;
      isInFlight = true;
      try {
        await axiosInstance.post(
          HEARTBEATS_PATH,
          { ...activity, path: pathRef.current, isResume },
          {
            timeout: REQUEST_TIMEOUT_MS,
            adapter: "fetch",
            fetchOptions: { keepalive: true },
          },
        );
        backoffMs = 0;
        if (isResume) {
          isResumePending = false;
        }
      } catch {
        // Deliberately swallowed: the shared client already logs failed
        // responses to Datadog, and a lost heartbeat must never reach the user.
        backoffMs =
          backoffMs === 0
            ? FIRST_BACKOFF_MS
            : Math.min(backoffMs * 2, MAX_BACKOFF_MS);
        backoffUntil = Date.now() + backoffMs;
      } finally {
        isInFlight = false;
      }
    };

    const resume = (): void => {
      isResumePending = true;
      void sendHeartbeat();
    };

    const onInput = (): void => {
      const now = Date.now();
      const wasIdle = isIdle(now);
      lastInputAt = now;
      if (wasIdle) {
        resume();
      }
    };

    const onVisibilityChange = (): void => {
      if (isHidden()) {
        return;
      }
      lastInputAt = Date.now();
      resume();
    };

    const onTick = (): void => {
      if (!isIdle(Date.now())) {
        void sendHeartbeat();
      }
    };

    INPUT_EVENTS.forEach((event) =>
      document.addEventListener(event, onInput, LISTENER_OPTIONS),
    );
    document.addEventListener("visibilitychange", onVisibilityChange);
    const intervalId = setInterval(onTick, HEARTBEAT_INTERVAL_MS);

    return () => {
      INPUT_EVENTS.forEach((event) =>
        document.removeEventListener(event, onInput, LISTENER_OPTIONS),
      );
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(intervalId);
    };
  }, [axiosInstance]);
};
